import type { layout } from "@app/domain";
import type { Gift, HeldGift, SeatInfo } from "@app/protocol";
import { useEffect, useRef, useState } from "react";
import { Slot } from "../canvas/Stage";
import { giftSpot } from "./GiftBadge";
import type { seatSlots } from "./seatOrder";
import { arrivals } from "./social";

const FLIGHT_MS = 700;

interface Flight {
  key: string;
  gift: Gift;
  to: string;
  from: { x: number; y: number };
  target: { x: number; y: number };
}

/** Where a player's gift sits on the canvas, from their seat's slot and the pod's size. */
function spotOf(
  canvas: layout.Canvas,
  slots: ReturnType<typeof seatSlots>,
  seats: readonly (SeatInfo | null)[],
  userId: string,
): { x: number; y: number } | null {
  const index = seats.findIndex((seat) => seat?.userId === userId);
  const slot = (Object.keys(slots) as (keyof typeof slots)[]).find((s) => slots[s] === index);
  if (slot === undefined) return null;
  const rect = canvas.elements[slot];
  const size = canvas.pod.size;
  const spot = giftSpot(size);
  return { x: rect.x + (rect.w - size) / 2 + spot.left, y: rect.y + spot.top };
}

function Flying({
  flight,
  side,
  onLanded,
}: {
  flight: Flight;
  side: number;
  onLanded: () => void;
}) {
  const ref = useRef<HTMLImageElement>(null);
  // The parent re-renders as flights land; the running animation must not restart.
  const landed = useRef(onLanded);
  landed.current = onLanded;
  useEffect(() => {
    const node = ref.current;
    const dx = flight.target.x - flight.from.x;
    const dy = flight.target.y - flight.from.y;
    const run = node?.animate(
      [
        { transform: "translate(-50%, -50%) scale(0.6)" },
        { transform: `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px)) scale(1)` },
      ],
      { duration: FLIGHT_MS, easing: "cubic-bezier(0.3, 0.7, 0.4, 1)", fill: "forwards" },
    );
    const done = () => landed.current();
    if (run === undefined) done();
    else run.onfinish = done;
    return () => run?.cancel();
  }, [flight]);
  return (
    <img
      ref={ref}
      src={`/gifts/${flight.gift}.webp`}
      alt=""
      aria-hidden="true"
      className="pointer-events-none absolute drop-shadow-[0_6px_10px_rgb(0_0_0/0.5)]"
      style={{ left: flight.from.x, top: flight.from.y, width: side, height: side }}
    />
  );
}

/**
 * Gifts sliding from their sender's pod to the receiver's picture (review 0022, the owner's Zynga
 * brief): no sound, no burst. `landing` holds the receivers whose badge waits for the flight.
 */
export function GiftFlights({
  canvas,
  slots,
  seats,
  gifts,
  onLanding,
}: {
  canvas: layout.Canvas;
  slots: ReturnType<typeof seatSlots>;
  seats: readonly (SeatInfo | null)[];
  gifts: Readonly<Record<string, HeldGift>>;
  onLanding: (landing: ReadonlySet<string>) => void;
}) {
  const before = useRef<Readonly<Record<string, HeldGift>> | null>(null);
  const [flights, setFlights] = useState<Flight[]>([]);
  useEffect(() => {
    const still =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const fresh = arrivals(before.current, gifts).flatMap((held) => {
      const from = spotOf(canvas, slots, seats, held.from);
      const target = spotOf(canvas, slots, seats, held.to);
      if (still || from === null || target === null || held.from === held.to) return [];
      return [{ key: `${held.to}-${held.at}`, gift: held.gift, to: held.to, from, target }];
    });
    before.current = gifts;
    if (fresh.length > 0) setFlights((now) => [...now, ...fresh]);
  }, [gifts, canvas, slots, seats]);
  useEffect(() => onLanding(new Set(flights.map((f) => f.to))), [flights, onLanding]);
  if (flights.length === 0) return null;
  const side = Math.max(26, Math.round(canvas.pod.size * 0.4));
  return (
    <Slot rect={{ x: 0, y: 0, w: canvas.w, h: canvas.h }} className="pointer-events-none z-40">
      {flights.map((flight) => (
        <Flying
          key={flight.key}
          flight={flight}
          side={side}
          onLanded={() => setFlights((now) => now.filter((f) => f.key !== flight.key))}
        />
      ))}
    </Slot>
  );
}
